import nodeCrypto from 'crypto'

// Runtime crypto helpers exposed to service code as `Flowrunner.crypto` — realtime `verify`/`verifyRequest`
// signature checks (09-realtime-triggers.md). Services can't `require` node modules, so HMAC, constant-time
// comparison, and timestamp freshness live here.

export interface FlowrunnerCrypto {
  hmac(algo: string, key: string, data: string, encoding?: 'hex' | 'base64'): string
  timingSafeEqual(a: unknown, b: unknown): boolean
  ageSeconds(timestamp: string | number): number
}

export const crypto: FlowrunnerCrypto = {
  hmac(algo, key, data, encoding = 'hex') {
    return nodeCrypto.createHmac(algo, key).update(data ?? '').digest(encoding)
  },

  // Constant-time string comparison; false (not throw) on nullish/length-mismatch so callers can `return`.
  timingSafeEqual(a, b) {
    if (typeof a !== 'string' || typeof b !== 'string') {
      return false
    }

    const bufferA = Buffer.from(a)
    const bufferB = Buffer.from(b)

    if (bufferA.length !== bufferB.length) {
      return false
    }

    try {
      return nodeCrypto.timingSafeEqual(bufferA, bufferB)
    } catch {
      return false
    }
  },

  // Seconds since `timestamp` (ISO string, or epoch seconds/ms — magnitude-detected).
  ageSeconds(timestamp) {
    const numeric = typeof timestamp === 'number' ? timestamp : Date.parse(timestamp) || Number(timestamp)
    const milliseconds = numeric < 1e12 ? numeric * 1000 : numeric

    return (Date.now() - milliseconds) / 1000
  },
}
